import { usePage } from "@inertiajs/react"
import { useEffect } from "react"

import { FormTokensPanel } from "@/components/dev/form_tokens_panel"
import { SpotlightLayout } from "@/layouts/spotlight-layout"
import { captureEvent } from "@/lib/analytics"

import { FormTabs } from "./components"
import type { TCheckinForm } from "./stores/useCheckinFormStore"
import { CheckinFormProvider } from "./stores/useCheckinFormStore"
import useContactStore from "./stores/useContactStore"
import { Demographics, Details, Facilities, SupportServices } from "./sub_forms"
import type { CheckinPageProps } from "./types"
import useAutoSave from "./use_auto_save"

interface CheckInFormProps extends Pick<
  CheckinPageProps,
  "booking" | "cohort" | "account"
> {
  lockVersion: number
  defaultData: Partial<TCheckinForm>
  readOnly: boolean
}

const CheckInForm = ({
  booking,
  cohort,
  account,
  lockVersion,
  defaultData,
  readOnly,
}: CheckInFormProps) => {
  const { onSubmit, conflict, lastSavedAt, saving } = useAutoSave({
    token: booking.token,
    lockVersion,
  })

  return (
    <>
      {/* Save status indicator */}
      <div className="notation flex items-center justify-end gap-2 px-6">
        {conflict && (
          <span className="text-caution-700">Conflict - please refresh</span>
        )}
        {saving && <span>Saving...</span>}
        {!saving && !conflict && lastSavedAt && (
          <span>Saved at {new Date(lastSavedAt).toLocaleTimeString()}</span>
        )}
      </div>
      <FormTabs.Group onSubmit={onSubmit}>
        <FormTabs.Item
          label="Booking Request"
          description="Confirm the session logistics"
        >
          <Details
            booking={booking}
            cohort={cohort}
            account={account}
            readOnly={true}
          />
        </FormTabs.Item>
        <FormTabs.Item
          label="School Facilities"
          description="Room setup and requirements"
        >
          <Facilities
            account={account}
            readOnly={readOnly}
            bookingContact={defaultData.booking_manager || ""} //Todo add booking manager to booking
          />
        </FormTabs.Item>
        <FormTabs.Item
          label="Cohort Demographics"
          description="Student background info"
        >
          <Demographics readOnly={readOnly} />
        </FormTabs.Item>
        <FormTabs.Item
          label="Follow On Support Services"
          description="Follow-on support planning"
        >
          <SupportServices
            account={account}
            bookingContact={defaultData.booking_manager || ""} //Todo add booking manager to booking
            readOnly={readOnly}
          />
        </FormTabs.Item>
      </FormTabs.Group>
    </>
  )
}

const CheckIn = () => {
  const {
    booking,
    cohort,
    form_submission: { answers, status, lock_version },
    account,
    school_contacts,
    analytics,
  } = usePage<CheckinPageProps>().props

  const setContacts = useContactStore((s) => s.set)

  useEffect(() => {
    setContacts(school_contacts)
  }, [school_contacts, setContacts])

  // The funnel's click-signal stand-in (BLU-10): open/click tracking is off by
  // decision, and this pageview needs JS execution, so SafeLinks prefetch
  // can't fire it. IDs only — no addresses, no answers.
  useEffect(() => {
    captureEvent(analytics, "checkin_form_viewed", {
      booking_id: booking.id,
      account_id: account.id,
    })
  }, [analytics, booking.id, account.id])

  return (
    <SpotlightLayout>
      <div className="flex flex-col gap-12">
        <CheckinFormProvider defaultData={answers}>
          <CheckInForm
            booking={booking}
            cohort={cohort}
            account={account}
            lockVersion={lock_version}
            defaultData={answers}
            readOnly={status === "submitted"}
          />
        </CheckinFormProvider>
      </div>
      <FormTokensPanel />
    </SpotlightLayout>
  )
}

export default CheckIn
